import { action, computed, observable } from 'mobx';

import {
    defaultRegistroImpuestoAgua,
    impuestosAguaDesgloseConceptoKeyNamePairs
} from './constants';

import {
    ImpuestosAguaDesglose,
    ImpuestosAguaDesgloseRecord,
    ImpuestosDesgloseRecord,
    ImpuestosDesgloseTotalRecord,
    SaneamientoAguaRecord
} from '../models';
import { setValueByPath, sum } from './utils';

import { registroImpuestosAguaStore as aguaStore } from './registroImpuestosAguaStore';

export class ImpuestosAguaDesgloseStore {
    kind: 'ingresos' | 'descuentos';

    @observable
    isSaving = false;

    constructor(kind: 'ingresos' | 'descuentos') {
        this.kind = kind;
    }

    @computed
    get isLoading() {
        return aguaStore.isLoading;
    }

    @computed
    get ejercicio() {
        return aguaStore.apertura.ejercicio;
    }

    @computed
    get desgloseEjercicioRecords(): ImpuestosDesgloseRecord[] {
        return this.toImpuestoAguaDesgloseRecords(this.getDesgloseEjercicio());
    }

    @computed
    get desgloseEjercicioAnteriorRecords(): ImpuestosDesgloseRecord[] {
        return this.toImpuestoAguaDesgloseRecords(this.getDesgloseEjercicioAnterior());
    }

    @computed
    get desgloseTotalRecords(): ImpuestosDesgloseTotalRecord[] {
        return this.toImpuesoAguaDesgloseTotalRecords(
            this.getDesgloseEjercicio(),
            this.getDesgloseEjercicioAnterior()
        );
    }

    @computed
    get saneamientoRecords(): SaneamientoAguaRecord[] {
        return this.toSaneamientoRecords(
            this.kind,
            this.getDesgloseEjercicio(),
            this.getDesgloseEjercicioAnterior()
        );
    }

    @action
    setDesgloseEjercicioValue = (path: string, value: number) => {
        setValueByPath(this.getDesgloseEjercicio(), path, value);
        aguaStore.markDirty();
    };

    @action
    setDesgloseEjercicioAnteriorValue = (path: string, value: number) => {
        setValueByPath(this.getDesgloseEjercicioAnterior(), path, value);
        aguaStore.markDirty();
    };

    @action
    saveDesglose = async () => {
        this.isSaving = true;
        const { estado, id } = aguaStore.registroImpuestos;
        const desglose = aguaStore[this.kind];
        const formNumber = this.kind === 'ingresos' ? 1 : 4;

        if (estado === 'Sin Captura' || id === 0) {
            await aguaStore.getNewForm();
        }
        const result = await aguaStore.saveForm(desglose, formNumber);
        aguaStore.unmarkDirty();

        this.isSaving = false;
        if (result === 'success') {
            aguaStore.registroImpuestos[this.kind].isSaved = true;
        }
    };

    private getDesgloseEjercicio = (): ImpuestosAguaDesglose =>
        this.kind === 'ingresos'
            ? aguaStore[this.kind].ingresosEjercicio
            : aguaStore[this.kind].descuentosEjercicio;

    private getDesgloseEjercicioAnterior = (): ImpuestosAguaDesglose =>
        this.kind === 'ingresos'
            ? aguaStore[this.kind].ingresosEjercicioAnterior
            : aguaStore[this.kind].descuentosEjercicioAnterior;

    private toImpuestoAguaDesgloseRecords(
        aguaDesglose: ImpuestosAguaDesglose
    ): ImpuestosAguaDesgloseRecord[] {
        return Object.entries(aguaDesglose)
            .filter(([concepto]) => concepto !== 'saneamiento')
            .map(([key, importe]) => ({
                key,
                concepto: impuestosAguaDesgloseConceptoKeyNamePairs.get(
                    key as keyof ImpuestosAguaDesglose
                ) as string,
                importe
            }));
    }

    private toSaneamientoRecords(
        type: string,
        typeEjercicio: ImpuestosAguaDesglose,
        typeEjercicioAnterior: ImpuestosAguaDesglose
    ): SaneamientoAguaRecord[] {
        const saneamiento = typeEjercicio.saneamiento;
        const saneamientoAnterior = typeEjercicioAnterior.saneamiento;

        return [
            {
                key: `${type}Ejercicio.saneamiento`,
                concepto: impuestosAguaDesgloseConceptoKeyNamePairs.get('saneamiento') as string,
                saneamiento,
                saneamientoAnterior,
                numeroDeSaneamientoAcumulado: saneamiento + saneamientoAnterior
            }
        ];
    }

    private toImpuesoAguaDesgloseTotalRecords = (
        desgloseEjercicio: ImpuestosAguaDesglose,
        desgloseEjercicioAnterior: ImpuestosAguaDesglose
    ): ImpuestosDesgloseTotalRecord[] => [
        {
            key: 'total',
            concepto: 'TOTAL',
            importe:
                Object.values(desgloseEjercicio).reduce(sum) +
                Object.values(desgloseEjercicioAnterior).reduce(sum) -
                desgloseEjercicio.saneamiento -
                desgloseEjercicioAnterior.saneamiento
        }
    ];
}

export const impuestosAguaIngresosStore = new ImpuestosAguaDesgloseStore('ingresos');
export const impuestosAguaDescuentosStore = new ImpuestosAguaDesgloseStore('descuentos');
